import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    IDLE, PENDING,
    LIST, ADD, EDIT,
    DANGER, SUCCESS,
} from "const"
import { SilSafetyFunction } from "server/models"
import { StateType, deepCopy, hasProjectAccess } from "client/utils"
import {
    PRM_SIL_SAFETY_FUNCTION,
    StoreType,
    SilSafetyFunctionList,
    SilSafetyFunctionFilters,

    LoadAllSilSafetyFunctions,
    DeleteSilSafetyFunction,
    SortSilSafetyFunctions,
    GetMaxSilSafetyFunctions,

    setSilSafetyFunctionList,
    setSilSafetyFunctionIds,
    setSilSafetyFunctionSort,
    setSilSafetyFunctionFilters,
    addModal,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    PageForm,
    Loader,
    Button,
    Feedback,
    Table,
    TableSearchWrapper,
    SortRows,
    DeleteRow,
} from "client/components"

import AccessDenied from "client/parts/accessDenied"
import SilSafetyFunctionSearch from "client/pages/search/silSafetyFunctionSearch"
import Columns from "./columns"


const List = () => {
    const project = useSelector((state: StoreType) => state.project.projectView)
    const baseRoute = useSelector((state: StoreType) => state.silSafetyFunction.baseRoute)

    const safetyFunctionList = useSelector((state: StoreType) => state.silSafetyFunction.safetyFunctionList)
    const safetyFunctionIds = useSelector((state: StoreType) => state.silSafetyFunction.safetyFunctionIds)
    const sortColumn = useSelector((state: StoreType) => state.silSafetyFunction.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.silSafetyFunction.sortOrder)
    const filters = useSelector((state: StoreType) => state.silSafetyFunction.filters)
    const reloadList = useSelector((state: StoreType) => state.silSafetyFunction.reloadList)

    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        LoadAllSilSafetyFunctions(project.id).then((res) => {
            dispatch(setSilSafetyFunctionList(res))
            setLoadState(IDLE)
        })
    }, [reloadList])

    useEffect(() => {
        const sortedIds = SortSilSafetyFunctions(Object.values(safetyFunctionList), sortColumn, sortOrder, filters)
        dispatch(setSilSafetyFunctionIds(sortedIds))
    }, [safetyFunctionList, sortColumn, sortOrder, filters])

    const onSort: SortRows = (column: keyof SilSafetyFunction, order) => {
        dispatch(setSilSafetyFunctionSort(column, order))
    }

    const onFilter = (newFilters: SilSafetyFunctionFilters) => {
        dispatch(setSilSafetyFunctionFilters(newFilters))
    }

    const onDelete: DeleteRow = (safetyFunction: SilSafetyFunction, callback) => {
        DeleteSilSafetyFunction(safetyFunction.id, project.id).then((removedRows) => {
            const success = removedRows === 1
            callback(success, () => {
                const safetyFunctionListCopy: SilSafetyFunctionList = safetyFunctionList ? deepCopy(safetyFunctionList) : {}
                delete safetyFunctionListCopy[safetyFunction.id]
                dispatch(setSilSafetyFunctionList(safetyFunctionListCopy))
                dispatch(setSilSafetyFunctionIds(safetyFunctionIds.filter((id) => id !== safetyFunction.id)))
            })
        })
    }


    // max number of safety functions
    const safetyFunctionsNumber = Object.keys(safetyFunctionList).length
    const { maxSafetyFunctions, safetyFunctionsFeedback } = GetMaxSilSafetyFunctions(packageSettings, userData, safetyFunctionsNumber, lang)

    const maxSafetyFunctionsReached = maxSafetyFunctions !== null && safetyFunctionsNumber >= maxSafetyFunctions
    const maxSafetyFunctionsExceeded = maxSafetyFunctions !== null && safetyFunctionsNumber > maxSafetyFunctions

    const columns = Columns()

    const listAccess = hasProjectAccess(PRM_SIL_SAFETY_FUNCTION, LIST)
    const addAccess = hasProjectAccess(PRM_SIL_SAFETY_FUNCTION, ADD)

    if (!listAccess) return <AccessDenied />
    return (
        <Container>
            <PageHeader>
                <PageTitle>{lang.safetyFunctions}</PageTitle>
            </PageHeader>
            <PageContent>
                {
                    loadState === PENDING
                    && <Loader />
                }
                <TableSearchWrapper>
                    <SilSafetyFunctionSearch
                        id="safetyFunctionList"
                        rightOffset={addAccess}
                        filters={filters}
                        onFilter={onFilter}
                    />
                    {
                        addAccess && (
                            <Button
                                variant="success"
                                disabled={maxSafetyFunctionsReached}
                                onClick={() => dispatch(addModal({
                                    url: `${baseRoute}/${EDIT}/0`,
                                }))}
                            >
                                {lang.add}
                            </Button>
                        )
                    }
                </TableSearchWrapper>
                <Table
                    columns={columns}
                    dataList={safetyFunctionList}
                    dataIds={safetyFunctionIds}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                    rowSize="md"
                    showPagination
                    onSort={onSort}
                    onDelete={onDelete}
                />
                <PageForm>
                    <Feedback variant={maxSafetyFunctionsExceeded ? DANGER : SUCCESS}>{safetyFunctionsFeedback}</Feedback>
                </PageForm>
            </PageContent>
        </Container>
    )
}
export default List
